import { Accessory } from 'src/common/prepareAccessories';
import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping';

export const generateRevitPluginXML = (
    isCombined: boolean,
    comboName: string,
    valveAccessories: Accessory<AccessoriesPageData['vssAccessories']['valveAccessories']['0']>[],
    actuatorName: string | null,
    actuatorAccessories: Accessory<AccessoriesPageData['vssAccessories']['valveAccessories']['0']>[],
    availableValveParameterIds: string[],
    availableActuatorParameterIds: string[],
) => {
    const distributeParams = (
        params: string[],
        availableIds: string[],
    ) => {
        const totalRequested = params.length;
        const totalAvailable = availableIds.length;
        const canUse = Math.min(totalRequested, totalAvailable);

        const count = Math.min(params.length, canUse);

        return params.slice(0, count).map((param, index) => ({
            param,
            id: availableIds[index],
        }));
    };

    const createProductCodes = (xmlDocument: Document, productLabel: string) => {
        const productCodesTag = xmlDocument.createElement('product_codes');

        const productLabelTag = xmlDocument.createElement('product_code');
        productLabelTag.setAttribute('area', 'ALL');
        productLabelTag.setAttribute('value', productLabel);
        productCodesTag.appendChild(productLabelTag);

        return productCodesTag;
    };

    const createAdditionalParams = (xmlDocument: Document, paramsWithIds: Array<{param: string; id: string}>) => {
        const additionalParamsTag = xmlDocument.createElement('additional_params');
        paramsWithIds.forEach(({ param, id }) => {
            const additionalParamTag = xmlDocument.createElement('param');
            additionalParamTag.setAttribute('id', id);
            additionalParamTag.setAttribute('value', param);
            additionalParamTag.setAttribute('shared', 'true');
            additionalParamTag.setAttribute('visible', 'true');
            additionalParamsTag.appendChild(additionalParamTag);
        });

        return additionalParamsTag;
    };

    const createVariant = (
        xmlDocument: Document,
        productLabel: string,
        paramsWithIds: Array<{param: string; id: string}>,
    ) => {
        const variantTag = xmlDocument.createElement('variant');
        variantTag.setAttribute('selected', '1');
        variantTag.appendChild(createProductCodes(xmlDocument, productLabel));
        variantTag.appendChild(createAdditionalParams(xmlDocument, paramsWithIds));
        return variantTag;
    };

    const createVariants = (
        xmlDocument: Document,
        productLabel: string,
        paramsWithIds: Array<{param: string; id: string}>,
    ) => {
        const variantsTag = xmlDocument.createElement('variants');
        variantsTag.appendChild(createVariant(xmlDocument, productLabel, paramsWithIds));
        return variantsTag;
    };

    // Prepare the parameters
    const valveParams = valveAccessories.map(
        ({ accessory: { item } }) => {
            const parser = new DOMParser();
            const description = parser.parseFromString(
                item.description?.localized.join(', ') || '',
                'text/html',
            );

            return `${item.Label}, ${description.body.textContent}`;
        },
    );

    const actuatorParams = actuatorAccessories?.map(
        ({ accessory: { item } }) => {
            const parser = new DOMParser();
            const description = parser.parseFromString(
                item.description?.localized.join(', ') || '',
                'text/html',
            );

            return `${item.Label}, ${description.body.textContent}`;
        },
    ) || [];

    const valveParamsWithIds = distributeParams(valveParams, availableValveParameterIds);
    const actuatorParamsWithIds = distributeParams(actuatorParams, availableActuatorParameterIds);

    const xmlString = '<valve></valve>';
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    const valveTag = xmlDoc.getElementsByTagName('valve')[0];

    valveTag.appendChild(createVariants(
        xmlDoc,
        comboName,
        valveParamsWithIds,
    ));

    if (actuatorName && !isCombined) {
        const actuatorTag = xmlDoc.createElement('actuator');

        actuatorTag.appendChild(
            createVariants(
                xmlDoc,
                actuatorName,
                actuatorParamsWithIds,
            ),
        );

        valveTag.appendChild(actuatorTag);
    }

    return new XMLSerializer().serializeToString(xmlDoc);
};
